import {
  type Agent,
  ApiDeploymentSettings,
  EmailDeploymentSettings,
  type EmbedAgent,
  EmbedDeploymentSettings,
  ExcelDeploymentSettings,
  GoogleSheetDeploymentSettings,
  OutlookAddInDeploymentSettings,
  PhoneCallDeploymentSettings,
  PowerPointDeploymentSettings,
  SlackDeploymentSettings,
  type TApi,
  TeamsDeploymentSettings,
  TelegramDeploymentSettings,
  TwilioDeploymentSettings,
  type TypedDeploymentResponse,
  WhatsappDeploymentSettings,
  WordDeploymentSettings
} from "@nexus/types"
import { DbEnum } from "@nexus/types/core"
import { AllowedFileFormats } from "@nexus/types/domain"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import type { RootState } from "@/store"

import { mergeEmbedDefaults } from "../views/embed/utils/mergeDefaults"

/**
 * Payload for setNestedField action.
 */
interface SetNestedFieldPayload {
  path: (string | number)[]
  value: unknown
}

/**
 * All possible deployment settings types (discriminated union)
 */
export type AnyDeploymentSettings =
  | SlackDeploymentSettings
  | TwilioDeploymentSettings
  | WhatsappDeploymentSettings
  | TeamsDeploymentSettings
  | TelegramDeploymentSettings
  | EmailDeploymentSettings
  | EmbedDeploymentSettings
  | PhoneCallDeploymentSettings
  | GoogleSheetDeploymentSettings
  | ExcelDeploymentSettings
  | WordDeploymentSettings
  | PowerPointDeploymentSettings
  | OutlookAddInDeploymentSettings
  | ApiDeploymentSettings
  | Record<string, unknown>

/**
 * Deployment form state: API-shaped deployment + UI meta.
 * Redux stores deployment as TypedDeploymentResponse shape; meta is UI-only.
 */
export interface DeploymentFormDeployment {
  id: string
  name: string
  description: string | null
  organizationId: string
  type: string
  agentId?: string
  agent?: EmbedAgent | null
  createdAt?: Date | string
  updatedAt?: Date | string | null
  phoneNumberId?: string | null
  oauthConnectionId?: string | null
  oauthConnection?: any
  apiKeyConnectionId?: string | null
  apiKeyConnection?: any
  isActive?: boolean
  deploymentSettings: AnyDeploymentSettings
}

export interface DeploymentFormMeta {
  loadingAgent: boolean
  agentWithSkills: Agent | null
  dirty: boolean
  lastSavedAt?: number | null
  error?: string | null
}

export interface DeploymentFormState {
  deployment: DeploymentFormDeployment
  meta: DeploymentFormMeta
}

const defaultDeployment: DeploymentFormDeployment = {
  id: "",
  name: "",
  description: null,
  organizationId: "",
  type: DbEnum.DeploymentType.EMBED,
  agent: null,
  isActive: true,
  deploymentSettings: {
    embedSettings: {
      uiVersion: 1,
      welcomeMessages: ["Welcome! How can I help you today?"],
      displayName: "",
      suggestedMessages: [],
      color: "#000000",
      showHeader: true,
      position: "bottom-right",
      textColor: "#000000",
      backgroundColor: "#ffffff",
      uiSettings: {
        appearance: "light",
        radius: "md",
        containerRadius: "md",
        bgPattern: "",
        primaryColor: "#000000",
        agentMessageColor: "#f3f4f6",
        agentMessageTextColor: "#1f2937",
        userMessageColor: "#3b82f6",
        userMessageTextColor: "#ffffff"
      },
      header: { showHeader: true, headerMessage: "" },
      footer: { showFooter: true, footerMessage: "" },
      bubble: {
        position: "bottom-right",
        borderRadius: "lg",
        backgroundColor: "#FFFFFF",
        borderColor: "#FFFFFF",
        borderWidth: 0,
        size: "medium"
      }
    },
    assistantSettings: {
      chatDefaultCommand: "",
      enabledSkills: [],
      enableKnowledges: false,
      enableInternetSearch: false,
      enableSendDocuments: false,
      sendDocumentsType: [AllowedFileFormats.PDF],
      sendDocumentsTasksEnabled: [],
      sendDocumentsConfigType: "SKILLS"
    },
    securitySettings: {
      visibility: "private",
      allowOnlyOnSpecificDomains: false,
      allowedDomains: [],
      rateLimiting: true,
      rateLimitingLimit: 10,
      rateLimitingInterval: 60
    },
    advancedSettings: {
      enableCommands: true,
      showToolsInput: false,
      showToolsOutput: false,
      enableDocumentsInChat: true,
      showDownloadAndCopyButtons: false,
      enableDescribeMode: false,
      enabledRefreshConversation: true
    }
  }
}

export const initialState: DeploymentFormState = {
  deployment: defaultDeployment,
  meta: {
    loadingAgent: true,
    agentWithSkills: null,
    dirty: false,
    lastSavedAt: null,
    error: null
  }
}

const deploymentFormSlice = createSlice({
  name: "deploymentForm",
  initialState,
  reducers: {
    setField: <K extends keyof DeploymentFormDeployment>(
      state: DeploymentFormState,
      action: PayloadAction<{ key: K; value: DeploymentFormDeployment[K] }>
    ) => {
      const { key, value } = action.payload
      state.deployment[key] = value
      if (state.meta.dirty === false) {
        state.meta.dirty = true
      }
    },
    setFields: (
      state: DeploymentFormState,
      action: PayloadAction<
        | Partial<DeploymentFormDeployment>
        | TApi["Deployments"]["Get"]["Response"]["data"]
        | TypedDeploymentResponse
      >
    ) => {
      const payload = { ...action.payload }
      if (payload.type === DbEnum.DeploymentType.EMBED && payload.deploymentSettings) {
        payload.deploymentSettings = mergeEmbedDefaults(payload.deploymentSettings)
      }

      Object.assign(state.deployment, payload)
    },
    setMetaField: <K extends keyof DeploymentFormMeta>(
      state: DeploymentFormState,
      action: PayloadAction<{ key: K; value: DeploymentFormMeta[K] }>
    ) => {
      state.meta[action.payload.key] = action.payload.value
      if (action.payload.key === "agentWithSkills") {
        state.meta.loadingAgent = false
        const agent = action.payload.value as Agent | null
        state.deployment.agent = agent
          ? {
              id: agent.id,
              first_name: agent.first_name,
              last_name: agent.last_name,
              profile_picture: agent.profile_picture,
              role: agent.role,
              details: agent.details ?? null
            }
          : null
      }
    },
    setNestedField(state: DeploymentFormState, action: PayloadAction<SetNestedFieldPayload>) {
      let target: any = state.deployment
      const { path, value } = action.payload
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i]
        if (key === undefined) continue
        if (!target[key] || typeof target[key] !== "object") {
          target[key] = {}
        }
        target = target[key]
      }
      const lastKey = path.at(-1)
      if (lastKey !== undefined) {
        target[lastKey] = value
      }
      if (state.meta.dirty === false) {
        state.meta.dirty = true
      }
    },
    resetForm: () => initialState
  }
})

export const { setField, setFields, resetForm, setMetaField, setNestedField } =
  deploymentFormSlice.actions
export default deploymentFormSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

/** Deployment object (API shape) */
export const selectDeploymentFormDeployment = (state: RootState) => state.deploymentForm.deployment

/** UI meta */
export const selectDeploymentFormMeta = (state: RootState) => state.deploymentForm.meta

/** Flattened view for consumers that expect { id, name, ..., meta } (backward compat) */
export const selectDeploymentFormFlattened = createSelector(
  [selectDeploymentFormDeployment, selectDeploymentFormMeta],
  (deployment, meta) => ({ ...deployment, meta })
)
